import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/features';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeyInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeyInput && honeyInput.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9phothn2trhb35gvfn0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '17起草视频',
    url: 'https://17draft.com',
    description: '专业视频起草制作平台，专为创作者打造的视频起草制作解决方案',
    email: 'contact@17draft.com',
    telephone: '400-888-8888',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '创意产业园A座',
      addressLocality: '北京',
      addressRegion: '北京',
      postalCode: '100000',
      addressCountry: 'CN',
    },
    foundingDate: '2024',
    sameAs: [
      'https://weibo.com/17draft',
      'https://space.bilibili.com/17draft',
    ],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于17起草视频',
    description: '17起草视频的品牌故事、发展历程与联系方式',
    url: 'https://17draft.com/about',
  };

  const stats = [
    { value: '50,000+', label: '注册创作者' },
    { value: '200万+', label: 'AI生成脚本' },
    { value: '98%', label: '用户满意度' },
    { value: '500+', label: '脚本模板' },
  ];

  const milestones = [
    { year: '2024.03', title: '产品立项', desc: '创始团队组建，开始产品研发' },
    { year: '2024.09', title: '内测上线', desc: '开启首批创作者内测邀请' },
    { year: '2025.01', title: '正式发布', desc: '17起草视频1.0版本正式面向公众' },
    { year: '2025.06', title: '用户破万', desc: '注册创作者突破10,000人' },
    { year: '2026.01', title: 'AI升级', desc: 'AI脚本引擎全新升级，支持20+视频类型' },
    { year: '2026.06', title: '蓬勃发展', desc: '用户突破50,000，持续迭代优化' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          <div className="w-full max-w-5xl mx-auto px-4 md:px-6 pb-16 md:pb-20">
            <div className="mb-6">
              <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            </div>

            <section className="mb-14 md:mb-20">
              <div className="text-center mb-10">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                  关于17起草视频
                </h1>
                <p className="text-sm md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                  我们致力于为每一位视频创作者提供专业、高效的视频起草制作工具，让创意不再受限于效率。
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center p-4 md:p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <p className="text-xl md:text-3xl font-heading font-bold text-primary-600 mb-1">
                      {stat.value}
                    </p>
                    <p className="text-xs md:text-sm text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-xl bg-background-100 border border-background-200/70 p-6 md:p-8 mb-12">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
                  <p>
                    17起草视频诞生于一个简单的想法：<strong>让视频创作更高效</strong>。创始团队由一群深耕视频创作领域多年的资深从业者组成，他们深知视频创作者在脚本策划、素材整理和初稿输出环节面临的效率困境。
                  </p>
                  <p>
                    传统的视频创作流程中，从灵感到成片往往需要经历漫长的脚本撰写、繁琐的素材整理和反复的修改迭代。创作者们花费大量时间在重复性工作上，而非投入到真正创造价值的创意本身。17起草视频正是为了解决这一痛点而生。
                  </p>
                  <p>
                    我们融合了先进的AI技术与对视频创作流程的深刻理解，打造了一个涵盖脚本生成、素材智能管理、协作起草和成片输出的一站式平台。我们的使命是<strong>让每一位创作者都能将更多精力投入到创意本身</strong>，而非被繁琐的流程所困扰。
                  </p>
                  <p>
                    自2024年成立以来，17起草视频已经服务了超过50,000名创作者，累计生成了超过200万份视频脚本。我们始终相信，<strong>好的工具能让好创意走得更远</strong>。
                  </p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
                  发展历程
                </h2>
                <div className="relative">
                  <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2"></div>
                  <div className="space-y-6 md:space-y-0">
                    {milestones.map((m, index) => (
                      <div
                        key={m.year}
                        className={`flex flex-col md:flex-row gap-4 md:gap-8 items-start ${
                          index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                        }`}
                      >
                        <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                          <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                            <span className="inline-block px-2.5 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-medium mb-2">
                              {m.year}
                            </span>
                            <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">{m.title}</h3>
                            <p className="text-xs text-foreground-600">{m.desc}</p>
                          </div>
                        </div>
                        <div className="hidden md:flex w-8 items-center justify-center shrink-0">
                          <div className="w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50"></div>
                        </div>
                        <div className="flex-1 hidden md:block"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-14 md:mb-20">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {testimonials.map((t) => (
                  <div key={t.id} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-10">
              <div className="lg:col-span-2">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
                  联系方式
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-600">contact@17draft.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">客服电话</p>
                      <p className="text-sm text-foreground-600">400-888-8888</p>
                      <p className="text-xs text-foreground-500">工作日 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">公司地址</p>
                      <p className="text-sm text-foreground-600">北京市朝阳区创意产业园A座</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 text-foreground-500 shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">官方微信</p>
                      <p className="text-sm text-foreground-600">17起草视频</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <div className="rounded-xl bg-background-100 border border-background-200/70 p-6 md:p-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-1">
                    给我们留言
                  </h2>
                  <p className="text-sm text-foreground-600 mb-6">
                    有任何问题或合作意向，欢迎随时联系我们
                  </p>

                  {formStatus === 'success' ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                        <i className="ri-check-line text-2xl"></i>
                      </div>
                      <p className="text-base font-heading font-semibold text-foreground-900 mb-1">提交成功！</p>
                      <p className="text-sm text-foreground-600">感谢您的留言，我们将在1-2个工作日内回复您。</p>
                    </div>
                  ) : (
                    <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                      {formStatus === 'error' && formError && (
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                          {formError}
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入留言主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows={5}
                          maxLength={500}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（500字以内）"
                        ></textarea>
                      </div>

                      <div className="honeypot-field" aria-hidden="true">
                        <label htmlFor="phone_alt">备用电话</label>
                        <input
                          type="text"
                          id="phone_alt"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          readOnly
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="w-full py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {formStatus === 'loading' ? '提交中...' : '提交留言'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}